// src/components/Jobs/JobFilters.jsx
import React, { useState, useEffect } from 'react';
import { provincesWithCities, getCitiesByProvince } from '../../utils/cities';
import { EXPERIENCE_LEVEL_OPTIONS } from '../../utils/experienceLevels';

const JobFilters = ({ filters, onFilterChange, onClearFilters }) => {
    const [availableCities, setAvailableCities] = useState([]);
    const [isCityDisabled, setIsCityDisabled] = useState(true);

    const provinces = Object.keys(provincesWithCities);
    const workTypes = ['Remote', 'Hybrid', 'On-site'];
    const dateOptions = [
        { value: '', label: 'Any' },
        { value: 'week', label: 'This Week' },
        { value: 'month', label: 'This Month' },
        { value: 'three-months', label: 'Next 3 Months' }
    ];

    useEffect(() => {
        if (filters.province) {
            const cities = getCitiesByProvince(filters.province);
            setAvailableCities(cities);
            setIsCityDisabled(false);
            if (filters.city && !cities.includes(filters.city)) {
                onFilterChange({ city: '' });
            }
        } else {
            setAvailableCities([]);
            setIsCityDisabled(true);
            if (filters.city) {
                onFilterChange({ city: '' });
            }
        }
    }, [filters.province]);

    return (
        <div className="bg-white rounded-xl p-6 shadow-md sticky top-24">
            <h3 className="text-lg font-bold text-gradpath-dark mb-4 flex items-center gap-2">
                <i className="fas fa-filter text-gradpath-green"></i> Filters
            </h3>
            
            {/* Province Filter */}
            <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-map-marker-alt text-gradpath-green mr-1"></i> Province
                </label>
                <select
                    value={filters.province || ''}
                    onChange={(e) => onFilterChange({ province: e.target.value, city: '' })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                >
                    <option value="">All Provinces</option>
                    {provinces.map(province => (
                        <option key={province} value={province}>{province}</option>
                    ))}
                </select>
            </div>

            {/* City Filter */}
            <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-city text-gradpath-green mr-1"></i> City
                    <span className="text-xs text-gray-400 ml-1">(Select province first)</span>
                </label>
                <select
                    value={filters.city || ''}
                    onChange={(e) => onFilterChange({ city: e.target.value })}
                    disabled={isCityDisabled}
                    className={`w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none ${
                        isCityDisabled ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                >
                    <option value="">All Cities</option>
                    {availableCities.map(city => (
                        <option key={city} value={city}>{city}</option>
                    ))}
                </select>
                {isCityDisabled && (
                    <p className="text-xs text-gray-400 mt-1">Please select a province first</p>
                )}
            </div>

            {/* Experience Level Filter - NEW */}
            <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-chart-line text-gradpath-green mr-1"></i> Experience Level
                </label>
                <select
                    value={filters.experienceLevel || ''}
                    onChange={(e) => onFilterChange({ experienceLevel: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                >
                    <option value="">All Levels</option>
                    {EXPERIENCE_LEVEL_OPTIONS.map(level => (
                        <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                </select>
            </div>
            
            {/* Work Type Filter */}
            <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-briefcase text-gradpath-green mr-1"></i> Work Type
                </label>
                <div className="space-y-2">
                    {workTypes.map(type => (
                        <label key={type} className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="workType"
                                value={type}
                                checked={filters.workType === type}
                                onChange={(e) => onFilterChange({ workType: e.target.value })}
                                className="w-4 h-4 text-gradpath-green focus:ring-gradpath-green"
                            />
                            <span className="text-sm text-gray-700">{type}</span>
                        </label>
                    ))}
                </div>
            </div>
            
            {/* Closing Date Filter */}
            <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-calendar-alt text-gradpath-green mr-1"></i> Closing Date
                </label>
                <select
                    value={filters.dateFilter || ''}
                    onChange={(e) => onFilterChange({ dateFilter: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                >
                    {dateOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </div>
            
            {/* Clear Filters Button */}
            <button
                onClick={onClearFilters}
                className="w-full border-2 border-gradpath-green text-gradpath-green font-semibold py-2 px-4 rounded-lg hover:bg-gradpath-green hover:text-white transition-all inline-flex items-center justify-center gap-2"
            >
                <i className="fas fa-eraser"></i> Clear Filters
            </button>
        </div>
    );
};

export default JobFilters;